// ListingContentChecks.jsx
// Pre-submit content checks that mirror the kinds of rejections Walmart's
// AI content reviewer issues, so they're caught in the form instead of
// after the feed comes back "failed":
//
//   1. Capacity consistency: title vs. volumeCapacity vs. short/long
//      description vs. key features. Catches "title says 25oz, description
//      says 16 ounces". One click rewrites every mention to the size the
//      seller confirms.
//   2. Numbered-edition consistency: Book / Season / Volume / Series /
//      Part / Chapter / Wave. Catches "title says Book Three, a key
//      feature says Book One". One click rewrites every mention.
//   3. Thin content: short/long descriptions too short to describe the
//      product, too few key features, or one-or-two-word features (the
//      LEGO-style "missing essential details" rejection). These become
//      AI-fill tasks so "Fill Missing with AI" rewrites them.
//
// All checks block "Add to Batch" the same way placeholder text does.
// They re-run after AI-fill, so AI output that reintroduces a mismatch is
// caught too.

import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";

/* ───────────── Capacity parsing ───────────── */

const UNIT_FACTORS = [
    [/^(fl\.?\s*oz\.?|fluid\s*ounces?|oz\.?|ounces?)$/i, 1],
    [/^(ml|millilit(?:er|re)s?)$/i, 0.033814],
    [/^(l|lit(?:er|re)s?)$/i, 33.814],
    [/^cups?$/i, 8],
    [/^gal(?:lon)?s?$/i, 128],
];

// "25oz", "16 ounces", "16-ounce", "750 ml", "1.5 L", "3 cups", "12 fl. oz"
const CAPACITY_RE = /\b(\d+(?:\.\d+)?)\s*-?\s*(fl\.?\s*oz\.?|fluid\s*ounces?|oz\.?|ounces?|ml|millilit(?:er|re)s?|lit(?:er|re)s?|l|cups?|gal(?:lon)?s?)(?![a-z])/gi;

// Skip "weighs 8 oz", "Net Wt 12 oz" — those are weight, not capacity.
const WEIGHT_CONTEXT = /(weigh|weight|net\s*wt)/i;
const isWeightContext = (text, index) => WEIGHT_CONTEXT.test(text.slice(Math.max(0, index - 25), index));

export const toFlOz = (n, unit) => {
    const num = parseFloat(n);
    if (!isFinite(num) || !unit) return null;
    const hit = UNIT_FACTORS.find(([re]) => re.test(String(unit).trim()));
    return hit ? num * hit[1] : null;
};

export function findCapacityMentions(text) {
    const s = String(text ?? "");
    const out = [];
    for (const m of s.matchAll(CAPACITY_RE)) {
        if (isWeightContext(s, m.index)) continue;
        const flOz = toFlOz(m[1], m[2]);
        if (flOz == null) continue;
        out.push({ raw: m[0], number: m[1], unit: m[2], flOz, index: m.index });
    }
    return out;
}

// 750 ml ≈ 25.4 oz should count as "the same" as 25 oz.
const sameCapacity = (a, b) => Math.abs(a - b) <= Math.max(0.6, 0.04 * Math.max(a, b));

const parseCapacity = (display) => {
    const m = findCapacityMentions(display)[0];
    return m ? { measure: m.number, unit: m.unit } : null;
};

// Rewrites every capacity mention in `text` to `target` ({measure, unit}).
// Same unit family keeps the seller's wording ("16 ounces" -> "25 ounces");
// different family is replaced outright ("473 ml" -> "25 oz").
export function rewriteCapacity(text, target) {
    if (!text) return text;
    const targetFactor = toFlOz(1, target.unit);
    return String(text).replace(CAPACITY_RE, (match, num, unit, offset, whole) => {
        if (isWeightContext(whole, offset)) return match;
        if (toFlOz(1, unit) === targetFactor) return match.replace(num, String(target.measure));
        return `${target.measure} ${target.unit}`;
    });
}

// sources: [{ label, id, text }] or [{ label, id, flOz, display }] for
// structured fields. Returns null when consistent, otherwise one cluster
// per distinct capacity found, with where each was found.
export function checkCapacityConsistency(sources) {
    const clusters = [];
    sources.forEach((src) => {
        const mentions = src.flOz != null
            ? [{ flOz: src.flOz, raw: src.display }]
            : findCapacityMentions(src.text);
        mentions.forEach((m) => {
            let c = clusters.find((cl) => sameCapacity(cl.flOz, m.flOz));
            if (!c) {
                c = { flOz: m.flOz, display: m.raw.trim(), labels: new Set(), ids: new Set() };
                clusters.push(c);
            }
            c.labels.add(src.label);
            c.ids.add(src.id);
        });
    });
    if (clusters.length < 2) return null;
    return clusters.map((c) => ({ flOz: c.flOz, display: c.display, labels: [...c.labels], ids: [...c.ids] }));
}

/* ───────────── Numbered editions (Book / Season / Volume...) ───────────── */

const NUM_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
const ROMAN = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9 };
const NUM_TO_WORD = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
const NUM_TO_ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"];

// "Book Three", "book 3", "BOOK THREE", "Season 2", "Vol. II", "Wave #4"
const EDITION_RE = /\b(book|season|volume|vol\.|series|part|chapter|wave)\s*#?\s*(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|ix|iv|v?i{1,3}|v)\b/gi;

const capWord = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const normEditionKind = (k) => k.toLowerCase().replace(/\.$/, "").replace(/^vol$/, "volume");
const editionNumber = (tok) => {
    const t = tok.toLowerCase();
    if (/^\d+$/.test(t)) return parseInt(t, 10);
    return NUM_WORDS[t] ?? ROMAN[t] ?? null;
};
const editionDisplay = (kind, n) => `${capWord(kind)} ${NUM_TO_WORD[n] || n}`;

export function findEditionMentions(text) {
    const s = String(text ?? "");
    const out = [];
    for (const m of s.matchAll(EDITION_RE)) {
        const n = editionNumber(m[2]);
        if (!n) continue;
        out.push({ kind: normEditionKind(m[1]), n, raw: m[0], index: m.index });
    }
    return out;
}

// Returns null when consistent, otherwise the clusters for the first
// kind (book/season/...) that has 2+ different numbers.
export function checkEditionConsistency(sources) {
    const byKind = {};
    sources.forEach((src) => {
        findEditionMentions(src.text).forEach((m) => {
            const clusters = (byKind[m.kind] = byKind[m.kind] || []);
            let c = clusters.find((cl) => cl.n === m.n);
            if (!c) {
                c = { kind: m.kind, n: m.n, display: editionDisplay(m.kind, m.n), labels: new Set(), ids: new Set() };
                clusters.push(c);
            }
            c.labels.add(src.label);
            c.ids.add(src.id);
        });
    });
    const conflict = Object.values(byKind).find((cl) => cl.length >= 2);
    if (!conflict) return null;
    return conflict.map((c) => ({ ...c, labels: [...c.labels], ids: [...c.ids] }));
}

// Rewrites every mention of `kind` to number `n`, keeping each mention's
// own style: "1" -> "3", "One" -> "Three", "ONE" -> "THREE", "I" -> "III".
export function rewriteEdition(text, kind, n) {
    if (!text) return text;
    return String(text).replace(EDITION_RE, (match, k, tok) => {
        if (normEditionKind(k) !== kind) return match;
        const t = tok.toLowerCase();
        let repl = /^\d+$/.test(t) ? String(n) : ROMAN[t] != null ? NUM_TO_ROMAN[n] : NUM_TO_WORD[n];
        if (!repl) repl = String(n);
        if (/[a-z]/i.test(tok)) {
            if (tok === tok.toUpperCase()) repl = repl.toUpperCase();
            else if (tok === tok.toLowerCase()) repl = repl.toLowerCase();
        }
        return match.slice(0, match.length - tok.length) + repl;
    });
}

/* ───────────── Thin-content thresholds ───────────── */

// Walmart recommends far more than this; these are floors below which
// its reviewer reliably rejects for "missing essential details".
const SHORT_DESC_MIN_WORDS = 40;
const LONG_DESC_MIN_WORDS = 60;
const MIN_KEY_FEATURES = 3;
const MIN_FEATURE_WORDS = 3;

const wordCount = (s) => String(s ?? "").trim().split(/\s+/).filter(Boolean).length;

/* ───────────── Hook ───────────── */

export function useListingContentChecks({ fields, values, setValues, groupFields, groupValues, setGroupValues }) {
    const [capacityIgnored, setCapacityIgnored] = useState(false);
    const [editionIgnored, setEditionIgnored] = useState(false);

    const pk = (f) => f.path.join(".");
    const idFor = (f) => `missing-${pk(f)}`;

    const refs = useMemo(() => {
        const byKey = (k) => fields.find((f) => f.key === k && !f.isMeasureUnit);
        return {
            title: byKey("productName"),
            short: byKey("shortDescription"),
            long: byKey("longDescription"),
            kfGroup: groupFields.find((g) => g.path[g.path.length - 1] === "keyFeatures"),
            kfScalar: byKey("keyFeatures"),
            cap: fields.find((f) => f.isMeasureUnit && /capacity/i.test(f.key)),
        };
    }, [fields, groupFields]);

    const readText = (f, vals) => (f ? String(vals[pk(f)] ?? "") : "");
    const readKeyFeatures = (vals, gvals) => {
        if (refs.kfGroup) return (gvals[pk(refs.kfGroup)] || []).map((e) => String(e?.value ?? ""));
        if (refs.kfScalar) return readText(refs.kfScalar, vals).split(",");
        return [];
    };
    const kfId = refs.kfGroup ? `missing-${pk(refs.kfGroup)}` : refs.kfScalar ? idFor(refs.kfScalar) : "content-issues";

    const readStructuredCapacity = (vals) => {
        if (!refs.cap) return null;
        const base = pk(refs.cap);
        const measure = vals[`${base}.measure`];
        const unit = vals[`${base}.unit`];
        const flOz = toFlOz(measure, unit);
        return flOz == null ? null : { flOz, display: `${measure} ${unit}` };
    };

    // Make sure there are at least MIN_KEY_FEATURES slots, so the existing
    // group-entry AI-fill path has empty entries to write into.
    useEffect(() => {
        if (!refs.kfGroup) return;
        const key = pk(refs.kfGroup);
        setGroupValues((prev) => {
            const entries = prev[key] || [];
            if (entries.length >= MIN_KEY_FEATURES) return prev;
            return { ...prev, [key]: [...entries, ...Array.from({ length: MIN_KEY_FEATURES - entries.length }, () => ({}))] };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refs.kfGroup]);

    const findCapacityConflict = (vals, gvals) => {
        const titleText = readText(refs.title, vals);
        const structured = readStructuredCapacity(vals);
        // Only run on products that are actually sold by capacity.
        if (!refs.cap && findCapacityMentions(titleText).length === 0) return null;

        const sources = [];
        if (structured) sources.push({ label: refs.cap.title || "Volume Capacity", id: idFor(refs.cap), ...structured });
        if (refs.title) sources.push({ label: "Title", id: idFor(refs.title), text: titleText });
        if (refs.short) sources.push({ label: "Short Description", id: idFor(refs.short), text: readText(refs.short, vals) });
        if (refs.long) sources.push({ label: "Long Description", id: idFor(refs.long), text: readText(refs.long, vals) });
        readKeyFeatures(vals, gvals).forEach((t, i) => sources.push({ label: `Key Feature #${i + 1}`, id: kfId, text: t }));
        return checkCapacityConsistency(sources);
    };

    const findEditionConflict = (vals, gvals) => {
        const sources = [];
        if (refs.title) sources.push({ label: "Title", id: idFor(refs.title), text: readText(refs.title, vals) });
        if (refs.short) sources.push({ label: "Short Description", id: idFor(refs.short), text: readText(refs.short, vals) });
        if (refs.long) sources.push({ label: "Long Description", id: idFor(refs.long), text: readText(refs.long, vals) });
        readKeyFeatures(vals, gvals).forEach((t, i) => sources.push({ label: `Key Feature #${i + 1}`, id: kfId, text: t }));
        return checkEditionConsistency(sources);
    };

    const findThinIssues = (vals, gvals) => {
        const issues = [];
        // Empty required fields are already counted by the form's own
        // required-field check — only flag text that exists but is too thin.
        [[refs.short, SHORT_DESC_MIN_WORDS], [refs.long, LONG_DESC_MIN_WORDS]].forEach(([f, min]) => {
            if (!f) return;
            const text = readText(f, vals).trim();
            const n = wordCount(text);
            if (text && n < min) {
                issues.push({ kind: "desc", field: f, label: `${f.title} too thin (${n}/${min} words)`, id: idFor(f), reason: `only ${n} words` });
            }
        });

        const feats = readKeyFeatures(vals, gvals);
        const filled = feats.map((t) => t.trim()).filter(Boolean);
        if (feats.length < MIN_KEY_FEATURES && (refs.kfGroup || refs.kfScalar)) {
            issues.push({ kind: "kf", label: `Key Features (need ${MIN_KEY_FEATURES}+, have ${filled.length})`, id: kfId });
        }
        filled.forEach((t) => {
            if (wordCount(t) < MIN_FEATURE_WORDS) {
                issues.push({ kind: "kf", label: `Key Feature "${t}" is too vague`, id: kfId });
            }
        });
        const seen = new Set();
        filled.forEach((t) => {
            const norm = t.toLowerCase().replace(/[^a-z0-9]/g, "");
            if (seen.has(norm)) issues.push({ kind: "kf", label: `Duplicate key feature "${t}"`, id: kfId });
            seen.add(norm);
        });
        return issues;
    };

    // Facts the AI must not contradict when writing copy.
    const listingFacts = (vals = values) => {
        const facts = {};
        const structured = readStructuredCapacity(vals);
        if (structured) facts.capacity = structured.display;
        else {
            const fromTitle = findCapacityMentions(readText(refs.title, vals))[0];
            if (fromTitle) facts.capacity = fromTitle.raw.trim();
        }
        const ed = findEditionMentions(readText(refs.title, vals))[0];
        if (ed) facts.edition = editionDisplay(ed.kind, ed.n);
        return facts;
    };

    // Extra AI-fill tasks for thin descriptions. Pure (takes the live
    // values) so handleFillWithAI's retry loop sees its own progress.
    const buildAiTasks = (vals, gvals) => {
        const facts = listingFacts(vals);
        const factLine = (facts.capacity
            ? ` The product's capacity is exactly ${facts.capacity}; never mention any other size or capacity.`
            : "")
            + (facts.edition
                ? ` This product is ${facts.edition}; never mention any other ${facts.edition.split(" ")[0].toLowerCase()} number.`
                : "");
        const descTasks = findThinIssues(vals, gvals)
            .filter((i) => i.kind === "desc")
            .map((i) => ({
                path: pk(i.field),
                title: `${i.field.title} (current text is too thin, ${i.reason}. Write a complete, specific description of at least ${i.field === refs.long ? LONG_DESC_MIN_WORDS : SHORT_DESC_MIN_WORDS} words: what the product is, its key features, components or materials, dimensions or size, who it's for, and its benefits. Only state facts consistent with the title and key features.${factLine})`,
                type: "string",
                enum_values: null,
            }));

        // Vague (too-short) key features: ask AI to rewrite each one in place.
        const kfTasks = [];
        if (refs.kfGroup) {
            const key = pk(refs.kfGroup);
            (gvals[key] || []).forEach((e, idx) => {
                const t = String(e?.value ?? "").trim();
                if (t && wordCount(t) < MIN_FEATURE_WORDS) {
                    kfTasks.push({
                        path: `${key}[${idx}].value`,
                        title: `Key Features, entry #${idx + 1}, field "Key Features" (current value "${t}" is too vague. Rewrite it as one specific feature plus its benefit, 8-25 words, keeping the same fact.${factLine}) (must be text, not a number)`,
                        type: "string",
                        enum_values: null,
                    });
                }
            });
        }
        return [...descTasks, ...kfTasks];
    };

    const capacityConflict = useMemo(
        () => (capacityIgnored ? null : findCapacityConflict(values, groupValues)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values, groupValues, refs, capacityIgnored]
    );
    const editionConflict = useMemo(
        () => (editionIgnored ? null : findEditionConflict(values, groupValues)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values, groupValues, refs, editionIgnored]
    );
    const thinIssues = useMemo(
        () => findThinIssues(values, groupValues),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values, groupValues, refs]
    );

    const applyCapacity = (cluster) => {
        const target = parseCapacity(cluster.display);
        if (!target) return;

        setValues((prev) => {
            const next = { ...prev };
            [refs.title, refs.short, refs.long, refs.kfScalar].forEach((f) => {
                if (f && next[pk(f)]) next[pk(f)] = rewriteCapacity(next[pk(f)], target);
            });
            if (refs.cap) {
                const base = pk(refs.cap);
                let unit = next[`${base}.unit`];
                if (!unit) {
                    const targetFactor = toFlOz(1, target.unit);
                    unit = (refs.cap.unitEnum || []).find((u) => toFlOz(1, u) === targetFactor) || "";
                    if (unit) next[`${base}.unit`] = unit;
                }
                const factor = toFlOz(1, unit);
                if (factor) next[`${base}.measure`] = String(+(cluster.flOz / factor).toFixed(2));
            }
            return next;
        });

        if (refs.kfGroup) {
            const key = pk(refs.kfGroup);
            setGroupValues((prev) => ({
                ...prev,
                [key]: (prev[key] || []).map((e) => (e?.value ? { ...e, value: rewriteCapacity(e.value, target) } : e)),
            }));
        }
        setCapacityIgnored(false);
    };

    const applyEdition = (cluster) => {
        const fix = (t) => rewriteEdition(t, cluster.kind, cluster.n);
        setValues((prev) => {
            const next = { ...prev };
            [refs.title, refs.short, refs.long, refs.kfScalar].forEach((f) => {
                if (f && next[pk(f)]) next[pk(f)] = fix(next[pk(f)]);
            });
            return next;
        });
        if (refs.kfGroup) {
            const key = pk(refs.kfGroup);
            setGroupValues((prev) => ({
                ...prev,
                [key]: (prev[key] || []).map((e) => (e?.value ? { ...e, value: fix(e.value) } : e)),
            }));
        }
        setEditionIgnored(false);
    };

    const missingItems = [
        ...(capacityConflict
            ? [{ label: `Capacity mismatch (${capacityConflict.map((c) => c.display).join(" vs ")})`, id: "content-issues" }]
            : []),
        ...(editionConflict
            ? [{ label: `${capWord(editionConflict[0].kind)} mismatch (${editionConflict.map((c) => c.display).join(" vs ")})`, id: "content-issues" }]
            : []),
        ...thinIssues.map((i) => ({ label: i.label, id: i.id })),
    ];

    return {
        capacityConflict,
        editionConflict,
        thinIssues,
        missingItems,
        issueCount: missingItems.length,
        applyCapacity,
        ignoreCapacity: () => setCapacityIgnored(true),
        applyEdition,
        ignoreEdition: () => setEditionIgnored(true),
        buildAiTasks,
        listingFacts,
    };
}

/* ───────────── Banner ───────────── */

export const ContentIssuesBanner = ({ checks, onJump }) => {
    const { capacityConflict, editionConflict, applyCapacity, ignoreCapacity, applyEdition, ignoreEdition } = checks;
    if (!capacityConflict && !editionConflict) return null;

    return (
        <div id="content-issues" className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 mb-5 text-xs text-amber-900 space-y-3">
            {capacityConflict && (
                <div>
                    <p className="font-bold flex items-center gap-1.5 mb-1.5">
                        <AlertCircle size={14} className="flex-shrink-0" />
                        Capacity doesn't match across this listing. Walmart rejects this.
                    </p>
                    <ul className="mb-2 space-y-0.5">
                        {capacityConflict.map((c) => (
                            <li key={c.display}>
                                <span className="font-bold">{c.display}</span> in {c.labels.join(", ")}
                            </li>
                        ))}
                    </ul>
                    <p className="mb-2">Check the physical product's label, then pick the true size. Every mention gets rewritten to match.</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {capacityConflict.map((c) => (
                            <button key={c.display} type="button" onClick={() => applyCapacity(c)}
                                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold">
                                Use {c.display} everywhere
                            </button>
                        ))}
                        <button type="button" onClick={ignoreCapacity}
                            className="px-2 py-1.5 text-amber-700 underline">
                            These are intentional, ignore
                        </button>
                    </div>
                    <p className="text-[10px] text-amber-700">
                        Also check the photos: remove any image (size charts, infographics) that shows a different capacity.
                    </p>
                </div>
            )}

            {editionConflict && (
                <div>
                    <p className="font-bold flex items-center gap-1.5 mb-1.5">
                        <AlertCircle size={14} className="flex-shrink-0" />
                        {capWord(editionConflict[0].kind)} number doesn't match across this listing. Walmart rejects this.
                    </p>
                    <ul className="mb-2 space-y-0.5">
                        {editionConflict.map((c) => (
                            <li key={c.display}>
                                <span className="font-bold">{c.display}</span> in {c.labels.join(", ")}
                            </li>
                        ))}
                    </ul>
                    <p className="mb-2">Check the packaging, then pick the correct one. Every mention gets rewritten to match.</p>
                    <div className="flex flex-wrap gap-1.5">
                        {editionConflict.map((c) => (
                            <button key={c.display} type="button" onClick={() => applyEdition(c)}
                                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold">
                                Use {c.display} everywhere
                            </button>
                        ))}
                        <button type="button" onClick={ignoreEdition}
                            className="px-2 py-1.5 text-amber-700 underline">
                            These are intentional, ignore
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};